import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { checkSession } from '../utils/check_session';


interface WithDashboardProps {
    collapsed: boolean;
    toggleCollapsed: () => void;
    theme: string;
    toggleTheme: () => void;
    handleNavigation: (path: string) => void;
    showUserMenu: boolean;
    toggleUserMenu: () => void;
    handleLogout: () => void;
    remainingTime: number;
    formatTime: (ms: number) => string;
    loading: boolean;
    isMobile: boolean;
}


const withDashboardLogic = <P extends object>(
    WrappedComponent: React.ComponentType<P & WithDashboardProps>
) => {
    return (props: P) => {
        const [collapsed, setCollapsed] = useState(false);
        const [isMobile, setIsMobile] = useState(window.innerWidth <= 780);
        const toggleCollapsed = () => setCollapsed((prev) => !prev);
        const navigate = useNavigate();
        const { isAuthenticated, logout, isLoading } = useAuth();
        const [showUserMenu, setShowUserMenu] = useState(false);
        const toggleUserMenu = () => setShowUserMenu(prev => !prev);
        const [remainingTime, setRemainingTime] = useState(3600000);
        const handleLogout = async () => {
            const authToken = localStorage.getItem('authToken');
            if (authToken && process.env.REACT_APP_BACKEND_URL) {
                try {
                    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${authToken}`
                        }
                    });
                } catch (error) {
                    console.error('Error saat mencatat waktu logout:', error);
                }
            }
            logout();
            toggleUserMenu();
            navigate('/login_page');
        };
        const [loading, setLoading] = useState(false);


        const formatTime = (ms: number) => {
            const totalSeconds = Math.floor(ms / 1000);
            const hours = Math.floor(totalSeconds / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            const seconds = totalSeconds % 60;
            return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        };

        useEffect(() => {
            const calculateRemainingTime = () => {
                const lastActivity = localStorage.getItem('lastActivity');
                if (!lastActivity) {
                    localStorage.setItem('lastActivity', new Date().toISOString());
                    return 3600000;
                }
                const lastActivityTime = new Date(lastActivity).getTime();
                const currentTime = Date.now();
                return Math.max(0, 3600000 - (currentTime - lastActivityTime));
            };

            const interval = setInterval(() => {
                const remaining = calculateRemainingTime();
                setRemainingTime(remaining);

                if (remaining <= 0) {
                    handleLogout();
                }
            }, 1000);

            return () => clearInterval(interval);
        }, [handleLogout]);


        const [theme, setTheme] = useState(localStorage.getItem('theme') || 'light');
        const toggleTheme = () => {
            const newTheme = theme === 'light' ? 'dark' : 'light';
            setTheme(newTheme);
            localStorage.setItem('theme', newTheme);
        };

        useEffect(() => {
            if (!isLoading && !isAuthenticated) {
                navigate('/login_page');
            }
        }, [isAuthenticated, isLoading, navigate]);

        useEffect(() => {
            if (!isAuthenticated) return;


            const cleanup = checkSession(logout);


            localStorage.setItem('lastActivity', new Date().toISOString());

            return cleanup;
        }, [isAuthenticated, logout]);


        const handleNavigation = (path: string) => {
            localStorage.removeItem('editData');
            setLoading(true);
            navigate(path);
            setTimeout(() => setLoading(false), 300);
        };

        useEffect(() => {
            const handleResize = () => setIsMobile(window.innerWidth <= 780);
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);


        return (
            <WrappedComponent
                {...props as P}
                collapsed={collapsed}
                toggleCollapsed={toggleCollapsed}
                theme={theme}
                toggleTheme={toggleTheme}
                handleNavigation={handleNavigation}
                showUserMenu={showUserMenu}
                toggleUserMenu={toggleUserMenu}
                handleLogout={handleLogout}
                remainingTime={remainingTime}
                formatTime={formatTime}
                loading={loading}
                isMobile={isMobile}
            />
        );
    };
};

export default withDashboardLogic;